/**
 * iOS-only OAuth bridge using ASWebAuthenticationSession.
 *
 * SFSafariViewController (the default Capacitor Browser plugin on iOS) does
 * not reliably hand custom-scheme redirects back to the host app — JS-triggered
 * `window.location.href = 'ai.panluma.app://...'` is silently blocked, leaving
 * the OAuth flow stuck. ASWebAuthenticationSession is Apple's purpose-built
 * OAuth surface: it watches for the registered callback scheme on any
 * navigation (including JS redirects from the trampoline page) and hands the
 * URL back to the app, auto-dismissing the system browser.
 *
 * Native implementation: apps/frontend/ios/App/App/WebAuthPlugin.swift
 */

import { registerPlugin } from '@capacitor/core';

export interface WebAuthPlugin {
  authenticate(options: {
    url: string;
    callbackScheme: string;
    prefersEphemeralWebBrowserSession?: boolean;
  }): Promise<{ callbackUrl: string }>;
}

export const WebAuth = registerPlugin<WebAuthPlugin>('WebAuth');

export class WebAuthCancelledError extends Error {
  constructor() {
    super('USER_CANCELLED');
    this.name = 'WebAuthCancelledError';
  }
}

/**
 * Opens the given OAuth authorization URL in ASWebAuthenticationSession and
 * resolves with the final custom-scheme callback URL. Throws
 * `WebAuthCancelledError` if the user dismisses the sheet.
 */
export async function runIOSWebAuth(authUrl: string, callbackScheme: string): Promise<string> {
  try {
    const { callbackUrl } = await WebAuth.authenticate({
      url: authUrl,
      callbackScheme,
    });
    return callbackUrl;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (message === 'USER_CANCELLED') {
      throw new WebAuthCancelledError();
    }
    throw err;
  }
}
